import React, { useCallback, useEffect, useMemo } from 'react';

import { di } from 'react-magnetic-di';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { useRovoAgentActionAnalytics } from '@atlaskit/rovo-agent-analytics/actions';
import { useChatModeState } from '@atlassian/conversation-assistant-chat-prompt-input/useChatModeState';
import { useChatModeStoreActions as useChatModeStoreActionsRefresh } from '@atlassian/conversation-assistant-chat-prompt-input/useChatModeStoreActions';
import { useCurrentPageAsContext } from '@atlassian/conversation-assistant-chat-prompt-input/useCurrentPageAsContext';
import { useRovoEntitlementActions } from '@atlassian/conversation-assistant-entitlement/controllers/rovo-entitlement';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import { setupSentryClient } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { useAssistanceService } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { useChatContextProvider } from '@atlassian/conversation-assistant-store/controllers/chat-context/store';
import { useChatSignalsActions } from '@atlassian/conversation-assistant-store/controllers/chat-signals';
import { useChatMessagesActions } from '@atlassian/conversation-assistant-store/controllers/chat/use-chat-messages';
import { useReconnectStoreActions } from '@atlassian/conversation-assistant-store/controllers/reconnect';
import {
	useStagingAreaActions,
	useStagingAreaState,
} from '@atlassian/conversation-assistant-store/controllers/staging-area';
import { useUIStoreActions } from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { useTasksStoreActions as useUnifiedTasksStoreActions } from '@atlassian/conversation-assistant-store/controllers/unified-tasks';
import { useUserPreferences } from '@atlassian/conversation-assistant-store/controllers/user-preferences';
import { useHighlightActionsEditorPlaceholder } from '@atlassian/conversation-assistant-ui-components/controllers/useHighlightActionsEditorPlaceholder';
import { useSpaceStoreActions } from '@atlassian/rovo-spaces/controllers/space-store';

import { useRovoChatCapabilities } from './common/hooks/useRovoChatCapabilities';
import { BackBehaviorOverrideProvider } from './common/ui/BackBehaviorOverrideProvider';
import { StoreErrorFlagProvider } from './common/ui/hooks/use-show-store-error-flag/StoreErrorFlagProvider';
import { useShowStoreErrorFlag } from './common/ui/hooks/use-show-store-error-flag/useShowStoreErrorFlag';
import { useGlobalConfig } from './common/utils/global-config/useGlobalConfig';
import { useUIConfig } from './common/utils/ui-config/useUIConfig';
import { useActiveAction } from './controllers/active-action';
import { useDraftDataAction } from './controllers/bridge-api/draft-data-action';
import { useEnvironmentAction } from './controllers/bridge-api/environment-action';
import { useMessageStreamAction } from './controllers/bridge-api/message-stream-action/useMessageStreamAction';
import { useChatInitiatorStoreActions } from './controllers/chat-initiator-store/useChatInitiatorStoreActions';
import { useChatMessageResearchActions } from './controllers/chat-message-research-store/useChatMessageResearchActions';
import { useOnNewConversationStartedWithSendMessage } from './controllers/chat-mode-store';
import { ChatStoreContainer } from './controllers/chat-store/ChatStoreContainer';
import { createViewDeadAirBroker } from './controllers/chat-store/createViewDeadAirBroker';
import type { ChatStoreContainerProps } from './controllers/chat-store/types';
import { useChatStore } from './controllers/chat-store/useChatStore';
import { useHumanMessageIdReplacement } from './controllers/chat-store/useHumanMessageIdReplacement';
import { useRegisterRovoChatContextProviderForEnterpriseHer } from './controllers/chat-store/useRegisterRovoChatContextProviderForEnterpriseHer';
import { shouldUseCurrentNonEditingPageAsContext } from './shouldUseCurrentNonEditingPageAsContext';
import { type ConversationAssistantConfig } from './types';
import { ViewDeadAirProvider } from './ui/view-dead-air-tracker/ViewDeadAirProvider';
import { getPackageInfo } from './utils/package-info';

/**
 * WARNING: This wrapper component initializes the Chat Store and can be rendered multiple times
 * within one application. DO NOT add any rendering logic (modals, overlays, UI components) here
 * as it will cause multiple instances to be rendered. Keep this component pure and only use it
 * for state initialization and context provision.
 *
 * If you need to add UI elements, place them in the appropriate render components instead.
 */
export const ConversationAssistantWrapper = ({
	onPromptSent,
	forceRestoreConversationId,
	children,
}: {
	onPromptSent?: ConversationAssistantConfig['onPromptSent'];
	forceRestoreConversationId?: string;
	children: React.ReactNode;
}): React.JSX.Element => {
	di(useAIMateExperienceTracker, useGlobalConfig);
	const showFlag = useShowStoreErrorFlag();
	const {
		getContentBody,
		getAdditionalContext,
		getStagingSpace,
		processMessageStreamAction,
		processMessageStreamComplete,
		processMessageStreamError,
		processMessageStreamActionClassification,
		broadcastConfirmedActions,
		broadcastStreamStatus,
	} = useMessageStreamAction();
	const { requestBrowserContent } = useEnvironmentAction();
	const { requestJiraContext } = useDraftDataAction();
	const experienceTracker = useAIMateExperienceTracker();
	const assistanceService = useAssistanceService();
	const { getIsMounted: getIsChatMounted } = useUIStoreActions();
	const { sendAnalyticsEvent, trackAIMAUEvent } = useAnalytics();
	const {
		browserContextSanitizer,
		enableBrowserContextBanner,
		elements,
		preserveUrlConversationId,
	} = useUIConfig();
	const { productHeader, siteId } = useGlobalConfig();
	const { currentContext } = useCurrentPageAsContext();
	const [{ currentConversationId }] = useChatStore();
	// Read the active conversation's "use current page as context" toggle from the
	// chat mode store so the chat-context provider can derive useCurrentBrowsingContext.
	const { useCurrentPageContext } = useChatModeState(currentConversationId);
	const chatContextStoreActions = useChatContextProvider({
		requestBrowserContent,
		requestJiraContext,
		getContentBody,
		getAdditionalContext,
		getStagingSpace,
		useCurrentNonEditingPageAsContext: shouldUseCurrentNonEditingPageAsContext({
			enableBrowserContextBanner,
			shouldShowContextBanner: elements?.shouldShowContextBanner,
			pageType: currentContext?.type,
		}),
		useCurrentPageContext,
	});
	useRegisterRovoChatContextProviderForEnterpriseHer(chatContextStoreActions, productHeader);
	const { setStatus, setConversationError } = useChatSignalsActions();
	const { getMessages, setMessages } = useChatMessagesActions();
	const { setIsRovoEnabled } = useRovoEntitlementActions();
	const { startReconnection, endReconnection, isReconnecting } = useReconnectStoreActions();

	useEffect(() => {
		setupSentryClient({
			packageVersion: getPackageInfo().version,
			product: productHeader,
		});
	}, [productHeader]);

	const {
		processStreamResearch,
		updateResearchMessageId,
		markIncompleteResearchesErrored,
		completeAllSteps,
		clearMessageResearch,
		addImplicitResearchPlan,
		resetHasReceivedAnswerPart,
	} = useChatMessageResearchActions();
	const { getChatInitiator, initChatInitiator } = useChatInitiatorStoreActions();
	const { insertHighlightActionsEditorPlaceholder } = useHighlightActionsEditorPlaceholder();
	const {
		setMessageIdSelectedForPreview,
		setStagingAreaMode,
		setInvocationIdSelectedForPreview,
		getMessageIdSelectedForPreview,
		getInvocationIdSelectedForPreview,
	} = useStagingAreaActions();
	const { forceSkipAutoConversationSelection } = useStagingAreaState();
	const onNewConversationStartingWithSendMessage = useOnNewConversationStartedWithSendMessage();
	const { preferences } = useUserPreferences();
	// TODO(ai_mate_store_messages_be_driven): remove this local storeUserMessages payload
	// value when BE user settings are fully rolled out; BE overrides it via the capability header.
	const getStoreUserMessagesPreference = useCallback(
		() => preferences.storeUserMessages,
		[preferences.storeUserMessages],
	);

	const { activeAction } = useActiveAction();

	// Skip auto conversation selection if we're already previewing a message or an action is pending.
	// When an action is set, the chat store must start with EMPTY_CONVERSATION_ID rather than
	// restoring the previous conversation from localStorage via autoSelectConversation.
	// Matters only on initialization.
	const skipAutoConversationSelection =
		!!getMessageIdSelectedForPreview?.() ||
		!!getInvocationIdSelectedForPreview?.() ||
		forceSkipAutoConversationSelection ||
		!!activeAction;

	const onTempMessageIdReplaced = useCallback(
		(oldId: string, newId: string) => {
			// Update messageIdSelectedForPreview when the message ID changes during streaming
			if (oldId === getMessageIdSelectedForPreview?.()) {
				setMessageIdSelectedForPreview(newId);
			}
		},
		[getMessageIdSelectedForPreview, setMessageIdSelectedForPreview],
	);

	const { onHumanMessageIdReplaced, registerHumanMessageIdReplacementHandler } =
		useHumanMessageIdReplacement();

	const { trackAgentEvent } = useRovoAgentActionAnalytics({});
	const { setReasoningMode, getBrowserDisplayContextActive } = useChatModeStoreActionsRefresh();
	const { setSmartContextLinkRecommendations, upsertConversationSpace } = useSpaceStoreActions();
	const onConversationSpacesHydrated: NonNullable<
		ChatStoreContainerProps['onConversationSpacesHydrated']
	> = useCallback(
		(_conversationId, spaces) => {
			spaces.forEach((space) => upsertConversationSpace(space));
		},
		[upsertConversationSpace],
	);

	// Build the unified tasks container dep consumed by chat-store actions.
	// Always assembled (cheap) so the container shape is stable across renders.
	// All read accessors are sweet-state actions so the dep object identity does not depend on store
	// state — chat-store consumers won't re-render on every task mutation.
	const unifiedTasksActions = useUnifiedTasksStoreActions();
	const unifiedTasksContainerDep = useMemo(
		() => ({
			createTaskFromStream: unifiedTasksActions.createTaskFromStream,
			setTaskProgressState: unifiedTasksActions.setTaskProgressState,
			setTaskTodoSnapshot: unifiedTasksActions.setTaskTodoSnapshot,
			ackTaskCompleted: unifiedTasksActions.ackTaskCompleted,
			mergeConversationChannelTasks: unifiedTasksActions.mergeConversationChannelTasks,
			getTaskById: unifiedTasksActions.getTaskById,
			getActiveTaskForConversation: unifiedTasksActions.getActiveTaskForConversation,
		}),
		[unifiedTasksActions],
	);

	const isViewDeadAirEnabled = fg('rovo_chat_track_view_dead_air');
	const viewDeadAirBroker = useMemo(
		() => (isViewDeadAirEnabled ? createViewDeadAirBroker() : null),
		[isViewDeadAirEnabled],
	);
	const capabilities = useRovoChatCapabilities();

	return (
		<>
			<ChatStoreContainer
				isGlobal
				preserveUrlConversationId={
					fg('rovo_chat_preserve_url_conversation_id') && preserveUrlConversationId
				}
				siteId={siteId}
				onPromptSent={onPromptSent}
				forceRestoreConversationId={forceRestoreConversationId}
				assistanceService={assistanceService}
				experienceTracker={experienceTracker}
				sendAnalyticsEvent={sendAnalyticsEvent}
				trackAIMAUEvent={trackAIMAUEvent}
				chatContextStoreActions={chatContextStoreActions}
				capabilities={capabilities}
				setChatStatus={setStatus}
				setConversationError={setConversationError}
				getMessages={getMessages}
				setMessages={setMessages}
				getIsChatMounted={getIsChatMounted}
				startReconnection={startReconnection}
				endReconnection={endReconnection}
				isReconnecting={isReconnecting}
				setRovoEnabled={setIsRovoEnabled}
				processStreamResearch={processStreamResearch}
				markIncompleteResearchesErrored={markIncompleteResearchesErrored}
				completeAllSteps={completeAllSteps}
				updateResearchMessageId={updateResearchMessageId}
				clearMessageResearch={clearMessageResearch}
				addImplicitResearchPlan={addImplicitResearchPlan}
				resetHasReceivedAnswerPart={resetHasReceivedAnswerPart}
				initChatInitiator={initChatInitiator}
				getChatInitiator={getChatInitiator}
				insertHighlightActionsEditorPlaceholder={insertHighlightActionsEditorPlaceholder}
				processMessageStreamAction={processMessageStreamAction}
				processMessageStreamComplete={processMessageStreamComplete}
				processMessageStreamError={processMessageStreamError}
				processMessageStreamActionClassification={processMessageStreamActionClassification}
				broadcastStreamStatus={broadcastStreamStatus}
				broadcastConfirmedActions={broadcastConfirmedActions}
				setStagingAreaMode={setStagingAreaMode}
				setMessageIdSelectedForPreview={setMessageIdSelectedForPreview}
				setInvocationIdSelectedForPreview={setInvocationIdSelectedForPreview}
				onTempMessageIdReplaced={onTempMessageIdReplaced}
				onHumanMessageIdReplaced={onHumanMessageIdReplaced}
				registerHumanMessageIdReplacementHandler={registerHumanMessageIdReplacementHandler}
				skipAutoConversationSelection={skipAutoConversationSelection}
				onNewConversationStartingWithSendMessage={onNewConversationStartingWithSendMessage}
				browserContextSanitizer={browserContextSanitizer}
				getStoreUserMessagesPreference={getStoreUserMessagesPreference}
				trackAgentEvent={trackAgentEvent}
				viewDeadAirBroker={viewDeadAirBroker}
				showFlag={showFlag}
				setReasoningMode={setReasoningMode}
				getBrowserDisplayContextActive={getBrowserDisplayContextActive}
				unifiedTasksActions={unifiedTasksContainerDep}
				onSpaceSourceRecommendations={setSmartContextLinkRecommendations}
				onConversationSpacesHydrated={onConversationSpacesHydrated}
			>
				<StoreErrorFlagProvider showFlag={showFlag}>
					<ViewDeadAirProvider value={viewDeadAirBroker} identity={currentConversationId}>
						<BackBehaviorOverrideProvider>{children}</BackBehaviorOverrideProvider>
					</ViewDeadAirProvider>
				</StoreErrorFlagProvider>
			</ChatStoreContainer>
		</>
	);
};
